import React, { useEffect, useRef } from 'react'
import WaveSurfer from 'wavesurfer.js/dist/wavesurfer.js'
import { observer } from 'mobx-react-lite'
import { getBlob, logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { showToast } from 'shared/ui'
import styles from './styles.module.scss'

type Props = {
  id: string
  url: string
  width?: number
  height?: number
  waveform: WaveSurfer | null
  setWaveform: (wave: WaveSurfer | null) => void
  setDuration?: (value: number) => void
  playing: boolean
  setPlaying: (value: boolean) => void
  hide?: boolean
  autoplay?: boolean
  onErrorLoad?: () => void
}

export const AudioWave = observer(
  ({
    id,
    url,
    width,
    height = 24,
    waveform,
    setWaveform,
    setDuration,
    playing,
    setPlaying,
    hide,
    autoplay,
    onErrorLoad,
  }: Props) => {
    const waveformElementRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (id !== uiStore.playAudioId && playing) {
        if (waveform) {
          waveform.pause()
          setPlaying(waveform?.isPlaying())
        }
      }
    }, [uiStore.playAudioId, playing])

    useEffect(() => {
      let w_cache: WaveSurfer | null = null

      try {
        if (!waveformElementRef.current) return

        const w = WaveSurfer.create({
          barWidth: 2,
          barRadius: 2,
          barGap: 1,
          height,
          cursorWidth: 0,
          cursorColor: '#068FF9',
          container: waveformElementRef.current,
          waveColor: '#ACB3B9',
          progressColor: '#068FF9',
          autoplay,
        })
        w_cache = w

        setWaveform(w)

        getBlob(url, (blob) => {
          w.loadBlob(blob)
            .then(() => {
              setDuration?.(w.getDuration())
            })
            .catch((e) => {
              showToast({
                type: 'error',
                title: 'Something went wrong. Please try again',
              })
              setPlaying(false)
              onErrorLoad?.()
              logger.error(e)
            })
          w.on('play', () => {
            uiStore.setPlayAudioId(id || url)
            setPlaying(true)
          })
          w.on('pause', () => setPlaying(false))
          w.on('interaction', (time) => {
            setDuration?.(time)
          })
          w.on('audioprocess', (time) => {
            if (time === w.getDuration()) {
              setPlaying(false)
              setDuration?.(w.getDuration())
            } else {
              setDuration?.(time)
            }
          })
        })
      } catch (e) {
        logger.error(e)
      }

      return () => {
        w_cache?.destroy()
        setWaveform(null)
      }
    }, [id, height])

    return (
      <div
        ref={waveformElementRef}
        className={styles.wrap}
        style={{ width: width || '100%', display: hide ? 'none' : '' }}
      />
    )
  }
)
